/**
 * Life4Earth V2 — Admin Content Cleanup (Chronicle hard-delete + restore)
 *
 * Lists EVERY chronicle — project chronicles, topic trackers, and how-tos —
 * including soft-deleted ones, and lets a sysadmin permanently hard-delete one
 * (with all dependent data) or restore a soft-deleted one.
 *
 *   GET    /api/chronicles/admin/all         (sysadmin; all, incl. trashed)
 *   DELETE /api/chronicles/{guid}/hard       (sysadmin; cascade)
 *   POST   /api/chronicles/{guid}/restore    (owner or sysadmin)
 *
 * English only (system admin UI per CLAUDE.md).
 */

import React, { useState, useCallback } from 'react';
import {
  fetchAdminChronicles,
  hardDeleteChronicle,
  restoreChronicle,
  type AdminChronicleSummary,
} from '../../api/chronicles';
import styles from './AdminPages.module.css';

const CATEGORY_LABEL: Record<string, string> = {
  project_chronicle: 'Chronicle',
  topic_tracker: 'Topic Tracker',
  how_to: 'How-To',
};

export default function AdminContentCleanup() {
  const [rows, setRows] = useState<AdminChronicleSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  const [confirmTarget, setConfirmTarget] = useState<AdminChronicleSummary | null>(null);
  const [confirmText, setConfirmText] = useState('');
  const [busyGuid, setBusyGuid] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [resultMsg, setResultMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // ── Load ─────────────────────────────────────────────────────
  const handleLoad = useCallback(async () => {
    setLoading(true);
    setError(null);
    setResultMsg(null);
    try {
      const data = await fetchAdminChronicles();
      setRows(data);
      setSearched(true);
    } catch (err: any) {
      setError(err?.message || 'Failed to load content');
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Client-side filter ───────────────────────────────────────
  const filtered = rows.filter((r) => {
    if (categoryFilter && r.categoryKey !== categoryFilter) return false;
    if (!searchTerm.trim()) return true;
    const q = searchTerm.toLowerCase();
    return (
      r.title.toLowerCase().includes(q) ||
      r.chronicleGuid.toLowerCase().includes(q) ||
      (r.ownerDisplayName || '').toLowerCase().includes(q) ||
      (r.initiatorDisplayName || '').toLowerCase().includes(q) ||
      (CATEGORY_LABEL[r.categoryKey] || r.categoryKey).toLowerCase().includes(q)
    );
  });

  const liveCount = rows.filter((r) => !r.deletedAt).length;
  const trashCount = rows.filter((r) => r.deletedAt).length;

  // ── Restore ──────────────────────────────────────────────────
  const handleRestore = useCallback(async (r: AdminChronicleSummary) => {
    setBusyGuid(r.chronicleGuid);
    setError(null);
    setResultMsg(null);
    try {
      await restoreChronicle(r.chronicleGuid);
      setRows((prev) => prev.map((x) => (x.chronicleGuid === r.chronicleGuid ? { ...x, deletedAt: null } : x)));
      setResultMsg(`"${r.title}" restored.`);
    } catch (err: any) {
      setError(err?.message || 'Restore failed');
    } finally {
      setBusyGuid(null);
    }
  }, []);

  // ── Hard delete ──────────────────────────────────────────────
  const handleHardDelete = useCallback(async () => {
    if (!confirmTarget) return;
    setDeleting(true);
    setError(null);
    setResultMsg(null);
    try {
      const res = await hardDeleteChronicle(confirmTarget.chronicleGuid);
      setRows((prev) => prev.filter((x) => x.chronicleGuid !== confirmTarget.chronicleGuid));
      const removed = Object.entries(res.recordsRemoved)
        .filter(([, v]) => v > 0)
        .map(([k, v]) => `${k}: ${v}`)
        .join(', ');
      setResultMsg(`${res.message}${removed ? ` — removed ${removed}` : ''}`);
      setConfirmTarget(null);
      setConfirmText('');
    } catch (err: any) {
      setError(err?.message || 'Hard delete failed');
    } finally {
      setDeleting(false);
    }
  }, [confirmTarget]);

  // ── Render ───────────────────────────────────────────────────
  return (
    <div>
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>Content Cleanup</h1>
          <p className={styles.pageSubtitle}>
            Project chronicles, topic trackers, and how-tos across all states (including trashed).
            Restore a soft-deleted item, or permanently hard-delete one with all dependent data.
            Hard delete is irreversible.
          </p>
        </div>
      </div>

      {error && <div className={`${styles.statusBar} ${styles.status_error}`}>{error}</div>}
      {resultMsg && <div className={`${styles.statusBar} ${styles.status_success}`}>{resultMsg}</div>}

      {/* Controls */}
      <div className={styles.section}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            className={styles.input}
            type="text"
            placeholder="Filter by title, GUID, owner, or type..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ flex: 1, minWidth: 220 }}
          />
          <select
            className={styles.input}
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            style={{ flex: '0 0 180px' }}
          >
            <option value="">All types</option>
            <option value="project_chronicle">Chronicle</option>
            <option value="topic_tracker">Topic Tracker</option>
            <option value="how_to">How-To</option>
          </select>
          <button className={styles.btnPrimary} onClick={handleLoad} disabled={loading}>
            {loading ? 'Loading...' : searched ? 'Refresh' : 'Load Content'}
          </button>
        </div>
        {searched && (
          <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 8, marginBottom: 0 }}>
            {filtered.length} of {rows.length} shown · {liveCount} live, {trashCount} in trash
          </p>
        )}
      </div>

      {/* Empty */}
      {searched && filtered.length === 0 && !loading && (
        <p className={styles.emptyMsg}>
          {rows.length === 0 ? 'No content found.' : 'No content matches your filter.'}
        </p>
      )}

      {/* Table */}
      {filtered.length > 0 && (
        <div className={styles.section} style={{ padding: 0 }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={{ ...thStyle, width: 110 }}>Type</th>
                <th style={thStyle}>Title</th>
                <th style={thStyle}>Owner</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>State</th>
                <th style={thStyle}>Created</th>
                <th style={{ ...thStyle, width: 150, textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const isTrashed = !!r.deletedAt;
                const busy = busyGuid === r.chronicleGuid;
                return (
                  <tr key={r.chronicleGuid} style={trStyle}>
                    <td style={tdStyle}>
                      <span style={typeBadgeStyle}>{CATEGORY_LABEL[r.categoryKey] || r.categoryKey}</span>
                    </td>
                    <td style={tdStyle}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: '#1a1a2e' }}>{r.title || '(untitled)'}</div>
                      <div style={{ fontSize: 11, color: '#9ca3af', fontFamily: 'monospace' }}>{r.chronicleGuid}</div>
                    </td>
                    <td style={{ ...tdStyle, fontSize: 13, color: '#4b5563' }}>{r.ownerDisplayName || r.initiatorDisplayName || '—'}</td>
                    <td style={{ ...tdStyle, fontSize: 13, color: '#4b5563' }}>{r.status}</td>
                    <td style={tdStyle}>
                      <span style={isTrashed ? trashStateStyle : liveStateStyle}>{isTrashed ? 'Trash' : 'Live'}</span>
                    </td>
                    <td style={{ ...tdStyle, fontSize: 12, color: '#6b7280' }}>
                      {r.createdAt ? new Date(r.createdAt).toLocaleDateString() : '—'}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center', whiteSpace: 'nowrap' }}>
                      {isTrashed && (
                        <button
                          onClick={() => handleRestore(r)}
                          disabled={busy}
                          style={restoreBtnStyle}
                          title={`Restore "${r.title}"`}
                        >
                          {busy ? '…' : 'Restore'}
                        </button>
                      )}
                      <button
                        onClick={() => { setConfirmTarget(r); setConfirmText(''); setError(null); setResultMsg(null); }}
                        style={deleteBtnStyle}
                        title={`Permanently delete "${r.title}"`}
                      >
                        &times;
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Hard-delete confirm */}
      {confirmTarget && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <h3 style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 700, color: '#991b1b' }}>
              Permanently Delete {CATEGORY_LABEL[confirmTarget.categoryKey] || 'Content'}
            </h3>
            <p style={{ fontSize: 13, color: '#4b5563', lineHeight: 1.5, margin: '0 0 12px' }}>
              This permanently deletes <strong>{confirmTarget.title}</strong> and ALL dependent data
              (key players, timeline, links, metrics, media, status updates, content sections, follows,
              classifications, and contexts).
            </p>
            <p style={{ fontSize: 13, color: '#991b1b', fontWeight: 600, margin: '0 0 12px' }}>
              This action cannot be undone.
            </p>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 16 }}>
              <div><strong>GUID:</strong> <code style={{ fontSize: 11 }}>{confirmTarget.chronicleGuid}</code></div>
              <div><strong>State:</strong> {confirmTarget.deletedAt ? 'In trash' : 'Live'}</div>
            </div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 4 }}>
              Type the title to confirm:
            </label>
            <input
              className={styles.input}
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={confirmTarget.title}
              autoFocus
              style={{ marginBottom: 16 }}
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className={styles.btnSecondary} onClick={() => { setConfirmTarget(null); setConfirmText(''); }} disabled={deleting}>
                Cancel
              </button>
              <button
                onClick={handleHardDelete}
                disabled={confirmText.trim() !== confirmTarget.title.trim() || deleting}
                style={{
                  ...hardDeleteBtnStyle,
                  opacity: confirmText.trim() !== confirmTarget.title.trim() || deleting ? 0.4 : 1,
                  cursor: confirmText.trim() !== confirmTarget.title.trim() || deleting ? 'default' : 'pointer',
                }}
              >
                {deleting ? 'Deleting...' : 'Permanently Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Inline styles (mirror AdminPillars) ──────────────────────
const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const thStyle: React.CSSProperties = {
  textAlign: 'start', padding: '10px 16px', fontSize: 11, fontWeight: 700, color: '#6b7280',
  textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid #e5e7eb', background: '#f9fafb',
};
const tdStyle: React.CSSProperties = { padding: '10px 16px', verticalAlign: 'middle' };
const trStyle: React.CSSProperties = { borderBottom: '1px solid #f3f4f6' };
const typeBadgeStyle: React.CSSProperties = {
  fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4, color: '#3730a3', background: '#e0e7ff',
  textTransform: 'uppercase', letterSpacing: '0.04em',
};
const liveStateStyle: React.CSSProperties = {
  fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4, color: '#166534', background: '#dcfce7',
};
const trashStateStyle: React.CSSProperties = {
  fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4, color: '#92400e', background: '#fef3c7',
};
const restoreBtnStyle: React.CSSProperties = {
  fontSize: 12, fontWeight: 600, padding: '5px 10px', marginInlineEnd: 8, color: '#166534',
  background: 'rgba(22,101,52,0.06)', border: '1px solid rgba(22,101,52,0.2)', borderRadius: 4, cursor: 'pointer',
};
const deleteBtnStyle: React.CSSProperties = {
  width: 28, height: 28, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
  color: '#ef4444', background: 'rgba(239, 68, 68, 0.06)', border: '1px solid rgba(239, 68, 68, 0.15)', borderRadius: 4, cursor: 'pointer',
};
const overlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
};
const modalStyle: React.CSSProperties = {
  background: '#ffffff', borderRadius: 10, padding: '28px 32px', maxWidth: 480, width: '90vw', boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
};
const hardDeleteBtnStyle: React.CSSProperties = {
  padding: '8px 20px', fontSize: 13, fontWeight: 600, color: '#ffffff', background: '#dc2626', border: 'none', borderRadius: 6,
};
